/**
 * Custom Request-a-Quote system (replaces the Storeify app).
 * Bucket lives entirely in localStorage and is independent of the Shopify cart.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'usedental_quote_v1';
  var DEFAULT_PAGE_URL = '/pages/quote-request';
  var DEFAULT_THANK_YOU_URL = '/pages/quote-thank-you';
  var SUBMISSION_MARKER_KEY = 'usedental_quote_submitted';
  var DEFAULT_ENDPOINT = 'https://api.web3forms.com/submit';
  var ATTRIBUTION_STORAGE_KEY = 'usedental_quote_attribution_v1';
  var ATTRIBUTION_TTL = 30 * 24 * 60 * 60 * 1000;
  var UTM_KEYS = ['utm_campaign', 'utm_source', 'utm_medium', 'utm_term', 'utm_content'];
  var currentAttribution = null;

  /* ----------------------------- money helpers ---------------------------- */

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }

    var value = '';
    var match = format.match(placeholder);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ' '); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /* -------------------------- campaign attribution ----------------------- */

  function readStoredAttribution() {
    try {
      var raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      var attribution = raw ? JSON.parse(raw) : null;
      if (!attribution || !attribution.captured_at) return null;

      if (Date.now() - attribution.captured_at > ATTRIBUTION_TTL) {
        window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
        return null;
      }

      return attribution;
    } catch (e) {
      return currentAttribution;
    }
  }

  function captureAttribution() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      currentAttribution = readStoredAttribution();
      return currentAttribution;
    }

    var hasUtm = UTM_KEYS.some(function (key) {
      return params.get(key) !== null && params.get(key) !== '';
    });

    if (!hasUtm) {
      currentAttribution = readStoredAttribution();
      return currentAttribution;
    }

    var attribution = {
      landing_page: window.location.href,
      original_referrer: document.referrer || '',
      captured_at: Date.now()
    };

    UTM_KEYS.forEach(function (key) {
      attribution[key] = params.get(key) || '';
    });

    currentAttribution = attribution;
    try {
      window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    } catch (e) {}

    return attribution;
  }

  function getAttribution() {
    return currentAttribution || readStoredAttribution() || {};
  }

  /* ----------------------------- bucket store ----------------------------- */

  var Bucket = {
    get: function () {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        var items = raw ? JSON.parse(raw) : [];
        return Array.isArray(items) ? items : [];
      } catch (e) {
        return [];
      }
    },
    set: function (items) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {}
      updateCount();
      document.dispatchEvent(new CustomEvent('quote:updated'));
    },
    add: function (item) {
      var items = this.get();
      var existing = null;
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].variantId) === String(item.variantId)) { existing = items[i]; break; }
      }
      if (existing) {
        existing.qty += item.qty;
      } else {
        items.push(item);
      }
      this.set(items);
    },
    remove: function (variantId) {
      var items = this.get().filter(function (i) { return String(i.variantId) !== String(variantId); });
      this.set(items);
    },
    updateQty: function (variantId, qty) {
      var items = this.get();
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].variantId) === String(variantId)) {
          items[i].qty = Math.max(1, qty);
          break;
        }
      }
      this.set(items);
    },
    clear: function () { this.set([]); },
    count: function () {
      return this.get().reduce(function (sum, i) { return sum + (parseInt(i.qty, 10) || 0); }, 0);
    },
    subtotal: function () {
      return this.get().reduce(function (sum, i) { return sum + (i.priceCents * i.qty); }, 0);
    }
  };

  function updateCount() {
    var count = Bucket.count();
    var nodes = document.querySelectorAll('[data-quote-count]');
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = count;
  }

  /* ------------------------------- modal ---------------------------------- */

  function pageUrl() {
    return (window.QUOTE_CONFIG && window.QUOTE_CONFIG.pageUrl) || DEFAULT_PAGE_URL;
  }

  function openModal(title) {
    var modal = document.querySelector('[data-quote-modal]');
    if (!modal) { window.location.href = pageUrl(); return; }
    var text = modal.querySelector('[data-quote-modal-text]');
    if (text) text.textContent = 'Add "' + title + '" to quote';
    var complete = modal.querySelector('[data-quote-complete]');
    if (complete) complete.setAttribute('href', pageUrl());
    modal.hidden = false;
    document.body.classList.add('quote-modal-open');
  }

  function closeModal() {
    var modal = document.querySelector('[data-quote-modal]');
    if (modal) modal.hidden = true;
    document.body.classList.remove('quote-modal-open');
  }

  function bindModal() {
    var modal = document.querySelector('[data-quote-modal]');
    if (!modal) return;
    modal.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-quote-modal-close')) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
  }

  /* --------------------------- product add-to-quote ----------------------- */

  function readProductData(wrapper) {
    var script = wrapper.querySelector('[data-quote-product]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }

  function selectedVariant(product) {
    var form = document.querySelector('[data-product-form]');
    var variant = null;
    if (form) {
      var idField = form.querySelector('[name="id"]');
      if (idField && idField.value) {
        for (var i = 0; i < product.variants.length; i++) {
          if (String(product.variants[i].id) === String(idField.value)) { variant = product.variants[i]; break; }
        }
      }
    }
    if (!variant) {
      for (var j = 0; j < product.variants.length; j++) {
        if (product.variants[j].available !== false) { variant = product.variants[j]; break; }
      }
    }
    return variant || product.variants[0];
  }

  function selectedQty() {
    var form = document.querySelector('[data-product-form]');
    if (form) {
      var qtyField = form.querySelector('[name="quantity"]');
      if (qtyField && parseInt(qtyField.value, 10) > 0) return parseInt(qtyField.value, 10);
    }
    return 1;
  }

  function bindProductButtons() {
    var buttons = document.querySelectorAll('[data-quote-add-btn]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (e) {
        e.preventDefault();
        var wrapper = this.closest('[data-quote-add]');
        var product = readProductData(wrapper);
        if (!product) return;
        var variant = selectedVariant(product);
        if (!variant) return;
        Bucket.add({
          id: product.id,
          variantId: variant.id,
          title: product.title,
          variantTitle: variant.title && variant.title !== 'Default Title' ? variant.title : '',
          sku: variant.sku || '',
          handle: product.handle,
          url: product.url,
          image: variant.image || product.image || '',
          priceCents: variant.price,
          qty: selectedQty()
        });
        openModal(product.title);
      });
    }
  }

  /* --------------------------- bucket page render ------------------------- */

  var submitted = false;

  function renderBucket() {
    var root = document.querySelector('[data-quote-request]');
    if (!root || submitted) return;

    var items = Bucket.get();
    var emptyEl = root.querySelector('[data-quote-empty]');
    var contentEl = root.querySelector('[data-quote-content]');

    if (!items.length) {
      if (emptyEl) emptyEl.hidden = false;
      if (contentEl) contentEl.hidden = true;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    if (contentEl) contentEl.hidden = false;

    var tbody = root.querySelector('[data-quote-items]');
    if (tbody) {
      var html = '';
      items.forEach(function (item) {
        var lineTotal = item.priceCents * item.qty;
        var imgSrc = item.image ? (item.image.indexOf('//') === 0 ? 'https:' + item.image : item.image) : '';
        html += '' +
          '<tr class="quote-items__row" data-quote-line="' + item.variantId + '">' +
            '<td class="quote-items__product">' +
              (imgSrc ? '<img class="quote-items__img" src="' + imgSrc + '" alt="">' : '<span class="quote-items__img quote-items__img--empty"></span>') +
              '<div class="quote-items__info">' +
                '<a class="quote-items__title" href="' + item.url + '">' + escapeHtml(item.title) + '</a>' +
                (item.variantTitle ? '<div class="quote-items__variant">' + escapeHtml(item.variantTitle) + '</div>' : '') +
                (item.sku ? '<div class="quote-items__sku">' + escapeHtml(item.sku) + '</div>' : '') +
              '</div>' +
            '</td>' +
            '<td class="quote-items__price" data-label="List Price">' + formatMoney(item.priceCents) + '</td>' +
            '<td class="quote-items__qty" data-label="Qty">' +
              '<div class="quote-qty">' +
                '<button type="button" class="quote-qty__btn" data-quote-qty-down aria-label="Decrease quantity">&minus;</button>' +
                '<input type="number" class="quote-qty__input" min="1" value="' + item.qty + '" data-quote-qty-input>' +
                '<button type="button" class="quote-qty__btn" data-quote-qty-up aria-label="Increase quantity">+</button>' +
              '</div>' +
            '</td>' +
            '<td class="quote-items__total" data-label="Total">' + formatMoney(lineTotal) + '</td>' +
            '<td class="quote-items__remove">' +
              '<button type="button" class="quote-remove" data-quote-remove aria-label="Remove item">&times;</button>' +
            '</td>' +
          '</tr>';
      });
      tbody.innerHTML = html;
    }

    var subtotal = Bucket.subtotal();
    setText(root, '[data-quote-subtotal]', formatMoney(subtotal));
    setText(root, '[data-quote-total]', formatMoney(subtotal));
  }

  function bindBucketEvents() {
    var root = document.querySelector('[data-quote-request]');
    if (!root) return;

    root.addEventListener('click', function (e) {
      var row = e.target.closest('[data-quote-line]');
      if (!row) return;
      var variantId = row.getAttribute('data-quote-line');
      var input = row.querySelector('[data-quote-qty-input]');

      if (e.target.hasAttribute('data-quote-remove')) {
        Bucket.remove(variantId);
      } else if (e.target.hasAttribute('data-quote-qty-up')) {
        Bucket.updateQty(variantId, (parseInt(input.value, 10) || 1) + 1);
      } else if (e.target.hasAttribute('data-quote-qty-down')) {
        Bucket.updateQty(variantId, (parseInt(input.value, 10) || 1) - 1);
      }
    });

    root.addEventListener('change', function (e) {
      if (!e.target.hasAttribute('data-quote-qty-input')) return;
      var row = e.target.closest('[data-quote-line]');
      Bucket.updateQty(row.getAttribute('data-quote-line'), parseInt(e.target.value, 10) || 1);
    });

    document.addEventListener('quote:updated', renderBucket);
  }

  /* ------------------------------ form logic ------------------------------ */

  function bindTypeSelection() {
    var root = document.querySelector('[data-quote-request]');
    if (!root) return;

    var buttons = root.querySelectorAll('[data-quote-type]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function () {
        var type = this.getAttribute('data-quote-type');
        for (var j = 0; j < buttons.length; j++) {
          buttons[j].classList.toggle('is-active', buttons[j] === this);
        }
        var forms = root.querySelectorAll('[data-quote-form]');
        for (var k = 0; k < forms.length; k++) {
          forms[k].hidden = forms[k].getAttribute('data-quote-form') !== type;
        }
      }.bind(buttons[i]));
    }
  }

  function bindConditionalFields() {
    var customerForm = document.querySelector('[data-quote-form="customer"]');
    if (!customerForm) return;

    var radios = customerForm.querySelectorAll('[name="hs_account"]');
    var jdeWrap = customerForm.querySelector('[data-quote-jde-wrap]');
    var jdeInput = jdeWrap ? jdeWrap.querySelector('input') : null;
    var createMsg = customerForm.querySelector('[data-quote-create-msg]');

    function sync() {
      var choice = '';
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) { choice = radios[i].value; break; }
      }
      if (jdeWrap) jdeWrap.hidden = choice !== 'have';
      if (jdeInput) {
        if (choice === 'have') { jdeInput.setAttribute('required', 'required'); }
        else { jdeInput.removeAttribute('required'); }
      }
      if (createMsg) createMsg.hidden = choice !== 'create';
    }

    for (var i = 0; i < radios.length; i++) radios[i].addEventListener('change', sync);
    sync();
  }

  function bindFormSubmit() {
    var forms = document.querySelectorAll('[data-quote-form]');
    for (var i = 0; i < forms.length; i++) {
      forms[i].addEventListener('submit', function (e) {
        e.preventDefault();
        if (!this.checkValidity()) { this.reportValidity(); return; }
        submitQuote(this);
      });
    }
  }

  /* ---------------------------- relay submission -------------------------- */

  function readConfig() {
    var el = document.querySelector('[data-quote-config]');
    var cfg = {};
    if (el) { try { cfg = JSON.parse(el.textContent); } catch (e) {} }
    return cfg;
  }

  function buildMessage(userType, fields, items) {
    var isDealer = userType === 'dealer';
    var lines = [];

    lines.push('A new quote has been placed!');
    lines.push('');
    lines.push('Quote Details:');
    lines.push('');

    var name = [fields.first_name, fields.last_name].filter(Boolean).join(' ');
    lines.push('Name: ' + name);
    lines.push('Customer Type: ' + (isDealer ? 'Dealer' : 'Customer'));
    lines.push('Phone Number: ' + (fields.phone || ''));
    lines.push('Email: ' + (fields.email || ''));

    var cityStateZip = [fields.city, fields.state].filter(Boolean).join(', ');
    if (fields.zip) cityStateZip += (cityStateZip ? ' ' : '') + fields.zip;
    var fullAddress = [fields.address, cityStateZip].filter(Boolean).join(', ');
    lines.push('Address: ' + fullAddress);

    if (fields.hs_rep) {
      lines.push('Henry Schein Representative: ' + fields.hs_rep);
    }

    if (!isDealer && fields.hs_account) {
      var acct = fields.hs_account === 'have'
        ? 'I have a Henry Schein Account'
        : 'I need to create a Henry Schein Account';
      lines.push('Henry Schein Account: ' + acct);
    }

    if (fields.jde_number) {
      var jdeLabel = isDealer ? 'Customer JDE Number' : 'Henry Schein Account / JDE Number';
      lines.push(jdeLabel + ': ' + fields.jde_number);
    }

    lines.push('Interested in Financing: ' + (fields.financing_interest ? 'Yes' : 'No'));
    lines.push('Quote created time: ' + new Date().toLocaleString());
    lines.push('');

    lines.push('Reserved Equipment:');
    var total = 0;
    items.forEach(function (item) {
      total += item.priceCents * item.qty;
      lines.push(
        item.qty + ' x ' + item.title +
        (item.variantTitle ? ' (' + item.variantTitle + ')' : '') +
        (item.sku ? ' — SKU ' + item.sku : '') +
        ' — ' + formatMoney(item.priceCents) + ' each = ' + formatMoney(item.priceCents * item.qty)
      );
    });
    lines.push('');
    lines.push('Total: ' + formatMoney(total));

    return lines.join('\n');
  }

  function submitQuote(form) {
    var config = readConfig();
    var userType = form.getAttribute('data-quote-form');
    var items = Bucket.get();
    var attribution = getAttribution();

    var fields = {};
    var data = new FormData(form);
    data.forEach(function (value, key) {
      if (key === 'botcheck') return;
      fields[key] = value;
    });

    var payload = {
      access_key: config.accessKey || '',
      subject: config.subject || 'New Quote Reservation - USEDental.com',
      from_name: 'USEDental.com Quote',
      replyto: fields.email || '',
      message: buildMessage(userType, fields, items),
      web_page_referral: attribution.landing_page || '',
      original_referrer: attribution.original_referrer || '',
      utm_campaign: attribution.utm_campaign || '',
      utm_source: attribution.utm_source || '',
      utm_medium: attribution.utm_medium || '',
      utm_term: attribution.utm_term || '',
      utm_content: attribution.utm_content || ''
    };
    if (config.ccRecipients && config.ccRecipients.length) {
      payload.ccemail = config.ccRecipients.join('; ');
    }

    var button = form.querySelector('[type="submit"]');
    var originalText = button ? button.textContent : '';
    if (button) { button.disabled = true; button.textContent = 'Sending...'; }

    var endpoint = config.endpoint || DEFAULT_ENDPOINT;
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json().catch(function () { return { success: r.ok }; }); })
      .then(function (res) {
        if (res && (res.success || res.ok)) {
          onSubmitSuccess();
        } else {
          onSubmitError(button, originalText, (res && res.message) || 'Something went wrong.');
        }
      })
      .catch(function () {
        onSubmitError(button, originalText, 'Network error. Please try again.');
      });
  }

  function onSubmitSuccess() {
    submitted = true;
    Bucket.clear();

    // The real thank-you page consumes this marker and fires Meta's Lead event
    // once. Direct visits and page refreshes therefore do not create leads.
    try { window.sessionStorage.setItem(SUBMISSION_MARKER_KEY, '1'); } catch (e) {}

    var config = readConfig();
    window.location.assign(config.thankYouUrl || DEFAULT_THANK_YOU_URL);
  }

  function onSubmitError(button, originalText, message) {
    if (button) { button.disabled = false; button.textContent = originalText; }
    window.alert(message);
  }

  /* ------------------------------- utilities ------------------------------ */

  function setText(root, selector, value) {
    var el = root.querySelector(selector);
    if (el) el.textContent = value;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* --------------------------------- init --------------------------------- */

  function init() {
    captureAttribution();
    updateCount();
    bindModal();
    bindProductButtons();
    bindBucketEvents();
    bindTypeSelection();
    bindConditionalFields();
    bindFormSubmit();
    renderBucket();

    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) { updateCount(); renderBucket(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.QuoteBucket = Bucket;
})();
